import React, { useEffect, useState } from 'react';
import { Music, VolumeX, Volume2 } from 'lucide-react';
import { getAudioState, subscribeAudioState, toggleMusic, playSound } from '../audio';

interface MusicToggleButtonProps {
  variant?: 'compact' | 'badge' | 'button';
  className?: string;
  showLabel?: boolean;
}

export const MusicToggleButton: React.FC<MusicToggleButtonProps> = ({
  variant = 'badge',
  className = '',
  showLabel = true,
}) => {
  const [audioState, setAudioState] = useState(getAudioState());

  useEffect(() => {
    const unsubscribe = subscribeAudioState(() => {
      setAudioState(getAudioState());
    });
    return () => unsubscribe();
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    playSound('click');
    toggleMusic();
  };

  const isEnabled = audioState.isMusicEnabled && !audioState.isRecordingActive;

  if (variant === 'compact') {
    return (
      <button
        type="button"
        onClick={handleClick}
        title={isEnabled ? 'Couper la musique' : 'Activer la musique'}
        className={`p-1.5 sm:p-2 rounded-lg border transition-all cursor-pointer flex items-center justify-center relative group ${
          isEnabled
            ? 'bg-emerald-950/80 hover:bg-emerald-900 border-emerald-500/70 text-emerald-300 shadow-[0_0_8px_rgba(16,185,129,0.3)] active:scale-95'
            : 'bg-slate-900/90 hover:bg-slate-800 border-red-500/60 text-red-400 shadow-[0_0_6px_rgba(239,68,68,0.2)] active:scale-95'
        } ${className}`}
        aria-label={isEnabled ? 'Couper la musique' : 'Activer la musique'}
      >
        {isEnabled ? (
          <div className="flex items-center gap-1">
            <Music className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400 animate-pulse" />
            <div className="flex items-end gap-0.5 h-3">
              <span className="w-0.5 h-2 bg-emerald-400 animate-[pulse_0.8s_ease-in-out_infinite]" />
              <span className="w-0.5 h-3 bg-emerald-300 animate-[pulse_0.6s_ease-in-out_infinite_0.2s]" />
              <span className="w-0.5 h-1.5 bg-emerald-400 animate-[pulse_0.9s_ease-in-out_infinite_0.4s]" />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <VolumeX className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-400" />
            <span className="text-[9px] font-bold text-red-400 uppercase tracking-tighter">OFF</span>
          </div>
        )}
      </button>
    );
  }

  if (variant === 'button') {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`w-full py-2.5 px-3 rounded-lg font-bold text-xs uppercase tracking-wider border-2 transition-all cursor-pointer flex items-center justify-between gap-2 shadow-md active:translate-y-0.5 ${
          isEnabled
            ? 'bg-gradient-to-r from-emerald-950 via-teal-900 to-emerald-950 hover:from-emerald-900 hover:to-teal-800 text-emerald-200 border-emerald-500 shadow-[2px_2px_0_#059669]'
            : 'bg-gradient-to-r from-slate-900 via-red-950 to-slate-900 hover:from-slate-800 hover:to-red-900 text-slate-300 border-red-500 shadow-[2px_2px_0_#dc2626]'
        } ${className}`}
      >
        <div className="flex items-center gap-2">
          {isEnabled ? (
            <Music className="w-4 h-4 text-emerald-400 animate-pulse" />
          ) : (
            <VolumeX className="w-4 h-4 text-red-400" />
          )}
          <span>{isEnabled ? 'Musique : Activée' : 'Musique : Coupée'}</span>
        </div>
        <span
          className={`text-[10px] px-2 py-0.5 rounded border font-mono font-bold ${
            isEnabled
              ? 'bg-emerald-900/80 text-emerald-300 border-emerald-400'
              : 'bg-red-950/80 text-red-300 border-red-500'
          }`}
        >
          {isEnabled ? 'ON' : 'OFF'}
        </span>
      </button>
    );
  }

  // Default: 'badge'
  return (
    <button
      type="button"
      onClick={handleClick}
      title={isEnabled ? 'Couper la musique' : 'Activer la musique'}
      className={`px-2.5 py-1 rounded-full border text-[10px] sm:text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 backdrop-blur-sm shadow-md active:scale-95 ${
        isEnabled
          ? 'bg-emerald-950/90 hover:bg-emerald-900 border-emerald-500/80 text-emerald-300 shadow-[0_0_10px_rgba(16,185,129,0.3)]'
          : 'bg-slate-950/90 hover:bg-slate-900 border-red-500/70 text-red-400 shadow-[0_0_8px_rgba(239,68,68,0.25)]'
      } ${className}`}
    >
      {isEnabled ? (
        <>
          <Music className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
          {showLabel && <span>Musique : ON</span>}
          <div className="hidden sm:flex items-end gap-0.5 h-2.5 ml-0.5">
            <span className="w-0.5 h-1.5 bg-emerald-400 animate-[pulse_0.8s_ease-in-out_infinite]" />
            <span className="w-0.5 h-2.5 bg-emerald-300 animate-[pulse_0.6s_ease-in-out_infinite_0.2s]" />
            <span className="w-0.5 h-1 bg-emerald-400 animate-[pulse_0.9s_ease-in-out_infinite_0.4s]" />
          </div>
        </>
      ) : (
        <>
          <VolumeX className="w-3.5 h-3.5 text-red-400" />
          {showLabel && <span>Musique : OFF</span>}
        </>
      )}
    </button>
  );
};
